import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';
import { resolveLink, isInternalLink } from '../../../../utils/linkResolver';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

/**
 * ButtonCtaRenderer - Render component for button-cta block
 */
const ButtonCtaRenderer = ({ block, theme = {}, username }) => {
  const { content } = block;
  const {
    heading,
    subheading,
    buttonText,
    buttonLink,
    size = 'large',
  } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);
  const isOwn = !window.location.pathname.startsWith('/me/');
  const resolvedHref = resolveLink(buttonLink, username, isOwn);
  const useRouterLink = isInternalLink(resolvedHref);

  const sizeClasses = {
    medium: 'px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]',
    large: 'px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg min-h-[48px] sm:min-h-[52px]',
  };

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className={`max-w-4xl mx-auto flex flex-col ${styles.contentAlignmentClass}`}>
        {/* Heading */}
        {heading && (
          <h3
            className={`${styles.headingSizeClass} font-bold mb-2 sm:mb-3`}
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            {heading}
          </h3>
        )}

        {/* Subheading */}
        {subheading && (
          <p
            className="text-base sm:text-lg mb-4 sm:mb-6 max-w-2xl whitespace-pre-wrap"
            style={{ color: styles.mutedColor, fontFamily: styles.bodyFontStyle }}
          >
            {subheading}
          </p>
        )}

        {/* Button */}
        {buttonText && (
          useRouterLink ? (
            <Link
              to={resolvedHref}
              className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all hover:opacity-90 ${sizeClasses[size]}`}
              style={styles.getButtonStyles()}
            >
              {buttonText}
              <ArrowRight size={size === 'large' ? 20 : 18} />
            </Link>
          ) : (
            <a
              href={resolvedHref}
              className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all hover:opacity-90 ${sizeClasses[size]}`}
              style={styles.getButtonStyles()}
            >
              {buttonText}
              <ArrowRight size={size === 'large' ? 20 : 18} />
            </a>
          )
        )}
      </div>
    </div>
  );
};

export default ButtonCtaRenderer;
